import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { filenameFromContentDisposition } from '../shared/file-download';
import {
  BugPreview,
  CancelledPipeline,
  CreateBugFromStepResultRequest,
  CreatedBug,
  CreatedTestRun,
  CreateTestRunRequest,
  ExecutionBugPreview,
  StartedTestRunExecution,
  TestCaseResult,
  TestRun,
  TestRunExecution,
  TestStepResult,
  TestRunListFilter,
  TestRunListItem,
  TestRunStats,
  TestRunTagCount,
  TestRunHandoverRequest,
  TestRunPauseState,
  TestStepResultAttachment,
  TestRunSettings,
  TriggeredPipeline,
  UpdateStepResultRequest,
  UpdateTestRunRequest,
} from '../models/test-run.model';
import { ProtocolDownload, ProtocolOptions, ProtocolTarget, TestProtocol } from '../models/test-protocol.model';
import { Page } from '../models/page.model';
import { TestResultsSummary, TestResultsSummaryQuery } from '../models/test-results-summary.model';
import { TestCaseRunHistory } from '../models/test-case-run-history.model';
import { ResultImportResult } from '../models/result-import.model';
import type { RunTarget } from './execution-job.service';

/** Ein Modul laeuft immer manuell als Probelauf (NEX-2433); der Rest folgt der Probelauf-Regel. */
export type ModuleTrialStart = Pick<
  CreateTestRunRequest,
  'testModuleId' | 'environment' | 'releaseId' | 'owner' | 'parameterValues'
> & { testModuleId: string };

export type ResultImportFormat = 'robot' | 'junit';

@Injectable({ providedIn: 'root' })
export class TestRunService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiBaseUrl}/api/v1/projects`;

  private runs(projectId: string): string {
    return `${this.baseUrl}/${projectId}/test-runs`;
  }

  /**
   * Legt einen oder mehrere Laeufe an (NEX-1035/1036). Aus einer Plan-Quelle
   * entsteht ein Lauf pro Plan — deshalb antwortet der Server immer mit einer
   * Liste, auch wenn nur einer entstand.
   */
  create(projectId: string, request: CreateTestRunRequest): Observable<CreatedTestRun[]> {
    return this.http.post<CreatedTestRun[]>(this.runs(projectId), request);
  }

  startModuleTrial(projectId: string, trial: ModuleTrialStart): Observable<CreatedTestRun[]> {
    return this.create(projectId, { ...trial, kind: 'MANUAL', sourceType: 'MODULE', trial: true });
  }

  /**
   * Paginierte, serverseitig gefilterte Testlauf-Uebersicht (NEX-1033/1034).
   * Leere Filterwerte werden weggelassen, damit sie nicht als Filter wirken.
   */
  listPaged(projectId: string, filter: TestRunListFilter = {}, page = 0, size = 20): Observable<Page<TestRunListItem>> {
    return this.http.get<Page<TestRunListItem>>(this.runs(projectId), {
      params: this.filterParams(filter).set('page', page).set('size', size),
    });
  }

  /**
   * Projektweite Kennzahlen, unabhaengig von Suche und Filtern (NEX-2551). Eigener Aufruf,
   * weil die Seiten-Antwort nur ihren Ausschnitt kennt und aus ihr keine
   * projektweite Zahl abzuleiten ist.
   */
  getStats(projectId: string): Observable<TestRunStats> {
    return this.http.get<TestRunStats>(`${this.runs(projectId)}/stats`);
  }

  getTags(projectId: string): Observable<TestRunTagCount[]> {
    return this.http.get<TestRunTagCount[]>(`${this.runs(projectId)}/tags`);
  }

  /** Leere Filterwerte weglassen, damit sie nicht als Filter wirken; `trial: false` ist ein Filter (nur Echtläufe). */
  private filterParams(filter: TestRunListFilter): HttpParams {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(filter)) {
      const sent = Array.isArray(value) ? value.join(',') : value;
      if (sent || sent === false) params = params.set(key, sent);
    }
    return params;
  }

  /** Loescht einen Lauf samt seiner Ergebnisse; verlangt Projekt-Admin-Rechte. */
  deleteRun(projectId: string, runId: string): Observable<void> {
    return this.http.delete<void>(`${this.runs(projectId)}/${runId}`);
  }

  /** Slim case-result list across all runs. */
  listCaseResults(projectId: string): Observable<TestCaseResult[]> {
    return this.http.get<TestCaseResult[]>(`${this.runs(projectId)}/case-results`);
  }

  /**
   * Aggregated testresults overview across all completed runs. Matrix rows are paginated
   * and optionally server-filtered; KPIs, trend and environments stay global.
   */
  getResultsSummary(projectId: string, query: TestResultsSummaryQuery = {}): Observable<TestResultsSummary> {
    let params = new HttpParams();
    if (query.page !== undefined) params = params.set('page', query.page);
    if (query.size !== undefined) params = params.set('size', query.size);
    if (query.sort) params = params.set('sort', query.sort);
    if (query.environment) params = params.set('environment', query.environment);
    if (query.source) params = params.set('source', query.source);
    if (query.result) params = params.set('result', query.result);
    if (query.releaseId) params = params.set('releaseId', query.releaseId);
    if (query.search) params = params.set('search', query.search);
    return this.http.get<TestResultsSummary>(`${this.baseUrl}/${projectId}/test-results/summary`, { params });
  }

  /** Chronological run history of a single test case (newest first). */
  getRunHistory(projectId: string, testCaseId: string): Observable<TestCaseRunHistory> {
    return this.http.get<TestCaseRunHistory>(`${this.baseUrl}/${projectId}/test-cases/${testCaseId}/run-history`);
  }

  /** Kopfdaten und Zusammenstellung eines Laufs fuer das Bearbeiten-Formular (NEX-1100). */
  getSettings(projectId: string, runId: string): Observable<TestRunSettings> {
    return this.http.get<TestRunSettings>(`${this.runs(projectId)}/${runId}/settings`);
  }

  /** Aendert die Kopfdaten eines Laufs (NEX-1100); antwortet mit dem neuen Stand. */
  update(projectId: string, runId: string, request: UpdateTestRunRequest): Observable<TestRunSettings> {
    return this.http.patch<TestRunSettings>(`${this.runs(projectId)}/${runId}`, request);
  }

  /** Fully nested run detail. */
  get(projectId: string, runId: string): Observable<TestRun> {
    return this.http.get<TestRun>(`${this.runs(projectId)}/${runId}`);
  }

  /** Alle Durchlaeufe eines Laufs, neuester zuerst (NEX-1037). Ohne Pagination. */
  listExecutions(projectId: string, runId: string): Observable<TestRunExecution[]> {
    return this.http.get<TestRunExecution[]>(`${this.runs(projectId)}/${runId}/executions`);
  }

  /**
   * Startet den naechsten Durchlauf (NEX-1037).
   *
   * <p>Der Endpunkt braucht keinen Rumpf, bekommt aber bewusst ein leeres Objekt:
   * der `RequestBodySizeLimitFilter` (NEX-637) weist jeden POST ohne
   * `Content-Length` mit 411 ab.
   *
   * <p>Ein Runner-Lauf (Quelle TCDL) braucht ein Ziel: den Rechner oder eine Anforderung (NEX-1601).
   */
  startExecution(
    projectId: string,
    runId: string,
    target?: RunTarget,
    retestOf?: string,
  ): Observable<StartedTestRunExecution> {
    return this.http.post<StartedTestRunExecution>(
      `${this.runs(projectId)}/${runId}/executions`,
      startBody(target, retestOf),
    );
  }

  /**
   * Stoesst die am Lauf hinterlegte Pipeline an (NEX-752). Jeder Aufruf legt einen
   * eigenen Durchlauf an — derselbe Endpunkt bedient das erstmalige Ausloesen und
   * „Erneut ausloesen".
   *
   * <p>Der leere Rumpf ist Absicht: der `RequestBodySizeLimitFilter` (NEX-637) weist
   * jeden POST ohne `Content-Length` mit 411 ab.
   */
  triggerPipeline(projectId: string, runId: string): Observable<TriggeredPipeline> {
    return this.http.post<TriggeredPipeline>(`${this.runs(projectId)}/${runId}/pipeline-trigger`, {});
  }

  /**
   * Bricht den laufenden Pipeline-Durchlauf ab (NEX-1235): der Lauf beim Anbieter endet,
   * der Durchlauf wird mit dem bis dahin gemeldeten Stand abgeschlossen.
   */
  cancelPipelineExecution(projectId: string, runId: string, executionId: string): Observable<CancelledPipeline> {
    return this.http.post<CancelledPipeline>(`${this.runs(projectId)}/${runId}/executions/${executionId}/cancel`, {});
  }

  /** Importiert einen Ergebnis-Report als neuen Lauf; ohne Umgebung oder Release bleibt der Lauf ohne diese Angabe. */
  importTestResults(
    projectId: string,
    format: ResultImportFormat,
    file: File,
    environment: string | null,
    releaseId: string | null = null,
  ): Observable<ResultImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    let params = new HttpParams();
    if (environment) params = params.set('environment', environment);
    if (releaseId) params = params.set('releaseId', releaseId);
    return this.http.post<ResultImportResult>(`${this.baseUrl}/${projectId}/test-results/import/${format}`, formData, {
      params,
    });
  }

  updateStepResult(
    projectId: string,
    stepResultId: string,
    request: UpdateStepResultRequest,
  ): Observable<TestStepResult> {
    return this.http.patch<TestStepResult>(`${this.runs(projectId)}/step-results/${stepResultId}`, request);
  }

  /**
   * Creates a bug in the project's active provider from a failed step result and
   * links it (NEX-776/781). Redmine is writable; a Jira-only project answers 501.
   */
  createBugFromStepResult(
    projectId: string,
    stepResultId: string,
    request: CreateBugFromStepResultRequest = {},
  ): Observable<CreatedBug> {
    return this.http.post<CreatedBug>(`${this.runs(projectId)}/step-results/${stepResultId}/bug`, request);
  }

  /**
   * Legt einen Vorgang aus einem gescheiterten Fall-Ergebnis an und verknuepft ihn
   * (NEX-1298). Gleiche Gestalt wie der Schritt-Weg; haengt am Ergebnis bereits ein
   * Vorgang, antwortet der Server mit 409 statt einen zweiten anzulegen.
   */
  createBugFromCaseResult(
    projectId: string,
    caseResultId: string,
    request: CreateBugFromStepResultRequest = {},
  ): Observable<CreatedBug> {
    return this.http.post<CreatedBug>(`${this.runs(projectId)}/case-results/${caseResultId}/bug`, request);
  }

  /** Was die Bug-Anlage an einem Fall-Ergebnis an den Vorgang haengt. */
  previewBugFromCaseResult(projectId: string, caseResultId: string): Observable<BugPreview> {
    return this.http.get<BugPreview>(`${this.runs(projectId)}/case-results/${caseResultId}/bug-preview`);
  }

  /** Was die Bug-Anlage an einem Durchlauf an den Vorgang haengt. */
  previewBugFromExecution(projectId: string, runId: string, executionId: string): Observable<ExecutionBugPreview> {
    return this.http.get<ExecutionBugPreview>(`${this.runs(projectId)}/${runId}/executions/${executionId}/bug-preview`);
  }

  /**
   * Legt einen Vorgang am Durchlauf an, wo es keinen fehlgeschlagenen Fall gibt (etwa ein
   * Build-Fehler). Gleiche Gestalt wie der Fall-Weg; haengt schon einer, antwortet der Server mit 409.
   */
  createBugFromExecution(
    projectId: string,
    runId: string,
    executionId: string,
    request: CreateBugFromStepResultRequest = {},
  ): Observable<CreatedBug> {
    return this.http.post<CreatedBug>(`${this.runs(projectId)}/${runId}/executions/${executionId}/bug`, request);
  }

  /**
   * Pausieren, Fortsetzen, Uebergeben und Uebernehmen eines manuellen Laufs
   * (NEX-1109/1110). Alle vier antworten mit demselben Pause-Zustand, den auch
   * der Runner-Zustand traegt — der Aufrufer schreibt die Antwort dort hinein,
   * statt neu zu laden.
   *
   * <p>Die Endpunkte ohne Rumpf bekommen ein leeres Objekt statt `null` — gleiche
   * Form wie {@link startExecution}, damit alle vier Aufrufe hier gleich aussehen.
   * Notwendig ist es nicht: der `RequestBodySizeLimitFilter` (NEX-637) weist nur
   * einen POST ohne jede `Content-Length` ab, und ein Rumpf-loser Browser-POST
   * setzt `Content-Length: 0`.
   */
  pause(projectId: string, runId: string): Observable<TestRunPauseState> {
    return this.http.post<TestRunPauseState>(`${this.runs(projectId)}/${runId}/pause`, {});
  }

  resume(projectId: string, runId: string): Observable<TestRunPauseState> {
    return this.http.post<TestRunPauseState>(`${this.runs(projectId)}/${runId}/resume`, {});
  }

  handover(projectId: string, runId: string, request: TestRunHandoverRequest): Observable<TestRunPauseState> {
    return this.http.post<TestRunPauseState>(`${this.runs(projectId)}/${runId}/handover`, request);
  }

  takeOver(projectId: string, runId: string): Observable<TestRunPauseState> {
    return this.http.post<TestRunPauseState>(`${this.runs(projectId)}/${runId}/take-over`, {});
  }

  /**
   * Bricht den Lauf mit Begruendung ab (NEX-1314/1315). Die Antwort ist der ganze Lauf
   * im Status {@code ABORTED} samt Begruendung — die Abschluss-Karte zeigt sie daraus.
   */
  abort(projectId: string, runId: string, reason: string): Observable<TestRun> {
    return this.http.post<TestRun>(`${this.runs(projectId)}/${runId}/abort`, { reason });
  }

  private stepResultAttachmentsUrl(projectId: string, stepResultId: string): string {
    return `${this.runs(projectId)}/step-results/${stepResultId}/attachments`;
  }

  listStepResultAttachments(projectId: string, stepResultId: string): Observable<TestStepResultAttachment[]> {
    return this.http.get<TestStepResultAttachment[]>(this.stepResultAttachmentsUrl(projectId, stepResultId));
  }

  uploadStepResultAttachment(
    projectId: string,
    stepResultId: string,
    file: File,
    executionId?: string,
  ): Observable<TestStepResultAttachment> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<TestStepResultAttachment>(this.stepResultAttachmentsUrl(projectId, stepResultId), formData, {
      params: executionId ? { executionId } : undefined,
    });
  }

  downloadStepResultAttachment(projectId: string, stepResultId: string, attachmentId: string): Observable<Blob> {
    return this.http.get(`${this.stepResultAttachmentsUrl(projectId, stepResultId)}/${attachmentId}`, {
      responseType: 'blob',
    });
  }

  deleteStepResultAttachment(projectId: string, stepResultId: string, attachmentId: string): Observable<void> {
    return this.http.delete<void>(`${this.stepResultAttachmentsUrl(projectId, stepResultId)}/${attachmentId}`);
  }

  getProtocolPreview(target: ProtocolTarget, options: ProtocolOptions): Observable<TestProtocol> {
    return this.http.get<TestProtocol>(`${this.protocolUrl(target)}/preview`, { params: { ...options } });
  }

  downloadProtocol(target: ProtocolTarget, options: ProtocolOptions): Observable<ProtocolDownload> {
    return this.http
      .get(this.protocolUrl(target), { params: { ...options }, responseType: 'blob', observe: 'response' })
      .pipe(
        map((response) => ({
          blob: response.body ?? new Blob(),
          filename: filenameFromContentDisposition(response.headers.get('Content-Disposition'), 'Testprotokoll.pdf'),
        })),
      );
  }

  private protocolUrl({ projectId, runId, executionId, caseResultId }: ProtocolTarget): string {
    return `${this.runs(projectId)}/${runId}/executions/${executionId}/results/${caseResultId}/protocol`;
  }
}

/** Ziel als Body-Felder: Rechner als `runnerId`, Etikett als `requiredLabels`; ohne Ziel nichts. */
export function runTargetBody(target?: RunTarget | null): { runnerId?: string; requiredLabels?: string[] } {
  if (!target) return {};
  return target.kind === 'runner' ? { runnerId: target.runnerId } : { requiredLabels: target.labels };
}

function startBody(
  target?: RunTarget,
  retestOf?: string,
): { runnerId?: string; requiredLabels?: string[]; retestOf?: string } {
  const body = runTargetBody(target);
  return retestOf ? { ...body, retestOf } : body;
}
